import { useState } from 'react'
import { X, Edit2, CheckSquare, Square } from 'lucide-react'
import { lotesService } from '../../services/lotesService'
import toast from 'react-hot-toast'

const fmt = (v) => Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

/**
 * Modal para alterar comissão de compra ou venda em batch.
 * Permite desmarcar lotes individuais para não alterar a comissão deles.
 * 
 * Props:
 * - tipo: 'compra' | 'venda'
 * - lotes: array de lotes com { id, numero_lote, vv, qtd, cc_pct, cv_pct, comissao_compra_custom, comissao_venda_custom }
 * - organizadorId: int
 * - onClose: () => void
 * - onSaved: () => void (callback após salvar para recarregar dados)
 */
export default function AlterarComissaoModal({ tipo, lotes, organizadorId, onClose, onSaved }) {
  const isCompra = tipo === 'compra'
  const label = isCompra ? 'Comissão de Compra' : 'Comissão de Venda'

  // Estado: novo % para aplicar
  const [novoPercentual, setNovoPercentual] = useState('')
  const [saving, setSaving] = useState(false)

  // Estado: IDs dos lotes selecionados (inicia com todos marcados)
  const [selectedIds, setSelectedIds] = useState(() => new Set(lotes.map(l => l.id)))

  const todosSelected = selectedIds.size === lotes.length
  const nenhumSelected = selectedIds.size === 0

  const toggleLote = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const toggleTodos = () => {
    if (todosSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(lotes.map(l => l.id)))
    }
  }

  // Percentual atual de cada lote
  const getPctAtual = (lote) => {
    if (isCompra) {
      return lote.comissao_compra_custom != null ? lote.comissao_compra_custom : lote.cc_pct
    }
    return lote.comissao_venda_custom != null ? lote.comissao_venda_custom : lote.cv_pct
  }

  const handleSalvar = async () => {
    const pct = parseFloat(novoPercentual)
    if (isNaN(pct) || pct < 0 || pct > 100) {
      toast.error('Informe um percentual válido (0 a 100)')
      return
    }

    if (nenhumSelected) {
      toast.error('Selecione pelo menos um lote')
      return
    }

    setSaving(true)
    try {
      const data = {
        lote_ids: Array.from(selectedIds),
      }
      if (isCompra) {
        data.comissao_compra_custom = pct
      } else {
        data.comissao_venda_custom = pct
      }

      await lotesService.alterarComissaoCustomBatch(organizadorId, data)
      toast.success(`${label} alterada para ${pct}% em ${selectedIds.size} lote${selectedIds.size > 1 ? 's' : ''}`)
      onSaved()
      onClose()
    } catch (err) {
      toast.error('Erro ao alterar comissão')
      console.error(err)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Edit2 className="w-5 h-5 text-blue-600" />
            <h2 className="text-base font-semibold text-gray-800">Alterar {label}</h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Lotes */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm text-gray-600">
              <span className="font-semibold text-blue-600">{selectedIds.size}</span> de {lotes.length} lote{lotes.length > 1 ? 's' : ''} selecionado{selectedIds.size !== 1 ? 's' : ''}
            </p>
            <button
              onClick={toggleTodos}
              className="flex items-center gap-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 transition-colors"
            >
              {todosSelected ? <CheckSquare className="w-3.5 h-3.5" /> : <Square className="w-3.5 h-3.5" />}
              {todosSelected ? 'Desmarcar todos' : 'Selecionar todos'}
            </button>
          </div>

          <div className="space-y-0.5 mb-4 max-h-48 overflow-y-auto border border-gray-100 rounded-lg p-2">
            {lotes.map(l => {
              const isSelected = selectedIds.has(l.id)
              return (
                <div
                  key={l.id}
                  onClick={() => toggleLote(l.id)}
                  className={`flex items-center gap-2 py-1.5 px-2 rounded cursor-pointer transition-colors text-sm ${
                    isSelected ? 'hover:bg-blue-50 bg-white' : 'hover:bg-gray-50 bg-gray-50/50 opacity-60'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleLote(l.id)}
                    onClick={e => e.stopPropagation()}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 flex-shrink-0"
                  />
                  <span className={`font-medium ${isSelected ? 'text-gray-800' : 'text-gray-400'}`}>
                    Lote {l.numero_lote}
                  </span>
                  <div className="flex items-center gap-3 text-gray-500 ml-auto">
                    <span className={isSelected ? '' : 'text-gray-300'}>{fmt(l.vv)}</span>
                    <span className={`font-semibold ${
                      !isSelected ? 'text-gray-300' :
                      (l.comissao_compra_custom != null || l.comissao_venda_custom != null) ? 'text-blue-600' : 'text-gray-700'
                    }`}>
                      {getPctAtual(l).toFixed(2)}%
                    </span>
                  </div>
                </div>
              )
            })}
          </div>

          {/* Input novo percentual */}
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <label className="block text-sm font-medium text-blue-800 mb-2">
              Novo percentual para os lotes selecionados:
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number"
                step="0.01"
                min="0"
                max="100"
                value={novoPercentual}
                onChange={e => setNovoPercentual(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleSalvar() }}
                className="flex-1 border border-blue-300 rounded-lg px-3 py-2 text-lg font-semibold text-center focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="0.00"
                autoFocus
              />
              <span className="text-lg font-bold text-blue-700">%</span>
            </div>
            {novoPercentual && !isNaN(parseFloat(novoPercentual)) && selectedIds.size > 0 && (
              <p className="text-xs text-blue-600 mt-2">
                Exemplo: Lote de {fmt(lotes.find(l => selectedIds.has(l.id))?.vv || 20000)} → {label}: {fmt((lotes.find(l => selectedIds.has(l.id))?.vv || 20000) * parseFloat(novoPercentual) / 100)}
              </p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 px-6 py-4 border-t border-gray-100">
          <button onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50">
            Cancelar
          </button>
          <button onClick={handleSalvar} disabled={saving || !novoPercentual || nenhumSelected}
            className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium disabled:opacity-50">
            {saving ? 'Salvando...' : `Aplicar ${novoPercentual || '0'}% em ${selectedIds.size} lote${selectedIds.size !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </div>
  )
}
